import React from 'react';
import {downloadTemplate as downloadTemplateApi} from '../api/insuranceApi';
import {downloadBlob} from '../utils/fileUtils';
import {MESSAGES} from '../constants/messages';
import '../styles/Instructions.css';

interface InstructionsProps {
  onClose: () => void;
}

const Instructions: React.FC<InstructionsProps> = ({ onClose }) => {
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const downloadTemplate = async () => {
    try {
      const blob = await downloadTemplateApi();
      downloadBlob(blob, 'insurance_template.xlsx');
    } catch (error) {
      console.error('Error downloading template:', error);
      alert(MESSAGES.ERROR_DOWNLOAD_TEMPLATE);
    }
  };

  return (
    <div className="instructions-overlay" onClick={handleOverlayClick}>
      <div className="instructions-card">
        <button className="close-btn" onClick={onClose}>×</button>
        
        <div className="instructions-header">
          <div className="icon-circle">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
              <polyline points="13 2 13 9 20 9"/>
            </svg>
          </div>
          <h2>Πώς να Χρησιμοποιήσετε τον Έλεγχο Ασφάλισης</h2>
          <p className="subtitle">Ακολουθήστε αυτά τα απλά βήματα για να επαληθεύσετε την ασφάλιση οχημάτων</p>
        </div>

        <div className="instructions-content">
          {/* How the System Works Section */}
          <div className="info-section">
            <div className="info-section-header">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="3"/>
                <path d="M12 1v6m0 6v6M5.64 5.64l4.24 4.24m4.24 4.24l4.24 4.24M1 12h6m6 0h6M5.64 18.36l4.24-4.24m4.24-4.24l4.24-4.24"/>
              </svg>
              <h3>Πώς λειτουργεί το σύστημα</h3>
            </div>
            <div className="info-box-content">
              <p>Το σύστημα επαλήθευσης ασφάλισης οχημάτων σας επιτρέπει να ελέγξετε αυτόματα την κατάσταση ασφάλισης πολλαπλών οχημάτων ταυτόχρονα. Ανεβάζετε ένα αρχείο Excel με τα στοιχεία των οχημάτων σας και το σύστημα επεξεργάζεται όλα τα οχήματα, αναζητώντας πληροφορίες ασφάλισης από επίσημες βάσεις δεδομένων.</p>
            </div>
          </div>

          {/* Excel File Structure Section */}
          <div className="info-section">
            <div className="info-section-header">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
              <h3>Δομή Αρχείου Excel</h3>
            </div>
            <p className="info-section-text">Η μορφή που πρέπει να έχει το αρχείο σας</p>
            <div className="excel-structure-table">
              <div className="excel-structure-header">
                <div className="excel-cell">A</div>
                <div className="excel-cell">B</div>
              </div>
              <div className="excel-row-header">
                <div className="excel-cell">ΑΦΜ</div>
                <div className="excel-cell">Αριθμός Κυκλοφορίας</div>
              </div>
              <div className="excel-row">
                <div className="excel-cell">1</div>
                <div className="excel-cell">123456789</div>
                <div className="excel-cell">ABC123</div>
              </div>
              <div className="excel-row">
                <div className="excel-cell">2</div>
                <div className="excel-cell">123456789</div>
                <div className="excel-cell">ΑΒΓ123</div>
              </div>
              <div className="excel-row">
                <div className="excel-cell">3</div>
                <div className="excel-cell">987654321</div>
                <div className="excel-cell">ΔΕΖ456</div>
              </div>
            </div>
          </div>

          <div className="step">
            <div className="step-number">1</div>
            <div className="step-content">
              <h3>Κατεβάστε το Πρότυπο</h3>
              <p>Κατεβάστε το πρότυπο Excel για να ξεκινήσετε με τη σωστή μορφή.</p>
              <button className="template-btn" onClick={downloadTemplate}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Κατέβασμα Κενού Προτύπου
              </button>
            </div>
          </div>

          <div className="step">
            <div className="step-number">2</div>
            <div className="step-content">
              <h3>Συμπληρώστε τα Στοιχεία Οχημάτων</h3>
              <p>Προσθέστε τα οχήματά σας στο πρότυπο:</p>
              <div className="format-example">
                <div className="format-row header">
                  <span>Στήλη A</span>
                  <span>Στήλη B</span>
                </div>
                <div className="format-row">
                  <span>ΑΦΜ</span>
                  <span>Αριθμός Κυκλοφορίας</span>
                </div>
                <div className="format-row example">
                  <span>123456789</span>
                  <span>ABC123</span>
                </div>
                <div className="format-row example">
                  <span>123456789</span>
                  <span>ΑΒΓ123</span>
                </div>
              </div>
            </div>
          </div>

          <div className="step">
            <div className="step-number">3</div>
            <div className="step-content">
              <h3>Ανέβασμα & Επεξεργασία</h3>
              <p>Ανεβάστε το συμπληρωμένο αρχείο Excel και πατήστε "Έλεγχος Ασφάλισης" για αυτόματη επαλήθευση όλων των οχημάτων.</p>
            </div>
          </div>

          <div className="step">
            <div className="step-number">4</div>
            <div className="step-content">
              <h3>Ανασκόπηση & Λήψη</h3>
              <p>Δείτε τα αποτελέσματα στον πίνακα και κατεβάστε την πλήρη αναφορά με όλες τις λεπτομέρειες ασφάλισης.</p>
            </div>
          </div>
        </div>

        <div className="instructions-footer">
          <div className="info-box">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="16" x2="12" y2="12"/>
              <line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
            <span>Υποστηριζόμενη μορφή: μόνο αρχεία .xlsx | Συνιστάται μέχρι 300 οχήματα ανά αρχείο</span>
          </div>
          <button className="start-btn" onClick={onClose}>
            Ξεκινήστε
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="5" y1="12" x2="19" y2="12"/>
              <polyline points="12 5 19 12 12 19"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Instructions;
